import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  Users, MapPin, Briefcase, SlidersHorizontal, X, FileText, Search,
  TrendingUp, Award, Mail, Send, Phone,
} from "lucide-react";
import InviteCandidateDialog from "../components/InviteCandidateDialog";
import useDocumentTitle from "../hooks/useDocumentTitle";

const POPULAR_SKILLS = [
  "Selenium", "Playwright", "Cypress", "API Testing", "Postman", "REST Assured",
  "JMeter", "TestNG", "JUnit", "Pytest", "Appium", "Java", "Python",
  "CI/CD", "Jenkins", "Cucumber", "Robot Framework", "Manual Testing",
];

export default function EmployerCandidates() {
  const { user } = useAuth();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myJobs, setMyJobs] = useState([]);
  const [filters, setFilters] = useState({
    skills: [],
    location: "",
    minExp: "",
    maxExp: "",
    jobId: "",
  });
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteCandidate, setInviteCandidate] = useState(null);
  useDocumentTitle("Browse candidates");

  useEffect(() => {
    api.get("/jobs/mine").then(({ data }) => setMyJobs(data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.skills.length > 0) params.skills = filters.skills.join(",");
      if (filters.location.trim()) params.location = filters.location.trim();
      if (filters.minExp !== "") params.min_experience = parseInt(filters.minExp, 10);
      if (filters.maxExp !== "") params.max_experience = parseInt(filters.maxExp, 10);
      if (filters.jobId) params.job_id = filters.jobId;
      const { data } = await api.get("/employer/candidates", { params });
      setCandidates(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load candidates");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const toggleSkill = (s) => {
    setFilters((prev) => ({
      ...prev,
      skills: prev.skills.includes(s) ? prev.skills.filter(x => x !== s) : [...prev.skills, s],
    }));
  };

  const clearAll = () => setFilters({ skills: [], location: "", minExp: "", maxExp: "", jobId: "" });

  const activeFilters = (filters.skills.length > 0 ? 1 : 0) + (filters.location ? 1 : 0) + (filters.minExp ? 1 : 0) + (filters.maxExp ? 1 : 0) + (filters.jobId ? 1 : 0);

  const downloadResume = async (user_id) => {
    try {
      const res = await api.get(`/resume/${user_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (err) {
      toast.error("This candidate hasn't uploaded a resume");
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest text-[#002FA7] font-bold mb-1">browse candidates</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-zinc-950">
            Find your next QA hire
          </h1>
          <p className="text-zinc-600 mt-1">Filter by skill, location, experience — and match against any of your open jobs.</p>
        </div>

        {/* Filters */}
        <Card className="p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <SlidersHorizontal className="h-4 w-4 text-[#002FA7]" />
              <span className="font-display text-sm font-bold tracking-tight text-zinc-950">Filters</span>
              {activeFilters > 0 && <Badge className="tag-mono bg-blue-50 text-blue-700 border border-blue-200">{activeFilters} active</Badge>}
            </div>
            {activeFilters > 0 && (
              <Button size="sm" variant="ghost" onClick={clearAll} data-testid="candidates-clear-filters" className="text-zinc-500 hover:text-zinc-900">
                <X className="h-3.5 w-3.5 mr-1" /> Clear
              </Button>
            )}
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <div>
              <Label className="text-xs">Match against my job</Label>
              <select
                value={filters.jobId}
                onChange={(e)=>setFilters(f=>({...f, jobId: e.target.value}))}
                data-testid="candidates-filter-job"
                className="mt-1.5 w-full h-10 rounded-md border border-zinc-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]/20"
              >
                <option value="">No specific job (use skills below)</option>
                {myJobs.map(j => (
                  <option key={j.job_id} value={j.job_id}>{j.title}{!j.is_active && " · (closed)"}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Location</Label>
              <Input
                value={filters.location}
                onChange={(e)=>setFilters(f=>({...f, location: e.target.value}))}
                placeholder="e.g. Bengaluru, Remote"
                data-testid="candidates-filter-location"
                className="mt-1.5 h-10"
              />
            </div>
            <div>
              <Label className="text-xs">Min years exp</Label>
              <Input
                type="number"
                min="0"
                value={filters.minExp}
                onChange={(e)=>setFilters(f=>({...f, minExp: e.target.value}))}
                placeholder="0"
                data-testid="candidates-filter-min-exp"
                className="mt-1.5 h-10"
              />
            </div>
            <div>
              <Label className="text-xs">Max years exp</Label>
              <Input
                type="number"
                min="0"
                value={filters.maxExp}
                onChange={(e)=>setFilters(f=>({...f, maxExp: e.target.value}))}
                placeholder="20"
                data-testid="candidates-filter-max-exp"
                className="mt-1.5 h-10"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs">Required skills (candidate must have ALL selected)</Label>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {POPULAR_SKILLS.map(s => {
                const active = filters.skills.includes(s);
                return (
                  <button
                    key={s}
                    onClick={()=>toggleSkill(s)}
                    data-testid={`candidates-filter-skill-${s}`}
                    className={`tag-mono px-2 py-1 rounded border transition-colors text-xs ${
                      active
                        ? "bg-[#002FA7] text-white border-[#002FA7]"
                        : "bg-white border-zinc-200 text-zinc-700 hover:border-[#002FA7] hover:text-[#002FA7]"
                    }`}
                  >
                    {active && "✓ "}{s}
                  </button>
                );
              })}
            </div>
          </div>
        </Card>

        <div className="flex items-center justify-between mb-4">
          <span className="text-sm text-zinc-500 tag-mono" data-testid="candidates-count">
            {loading ? "Loading…" : `${candidates.length} ${candidates.length === 1 ? "candidate" : "candidates"} found`}
          </span>
          {filters.jobId && candidates.length > 0 && (
            <span className="text-xs text-zinc-500 tag-mono">Sorted by match score against selected job</span>
          )}
        </div>

        {!loading && !user?.is_verified_employer && candidates.length > 0 && (
          <Card className="p-3 mb-4 border-amber-200 bg-amber-50" data-testid="verified-only-phone-hint">
            <div className="flex items-start gap-3 text-xs text-amber-900">
              <Phone className="h-4 w-4 flex-shrink-0 mt-0.5 text-amber-700" />
              <div>
                <span className="font-semibold">Phone numbers are hidden.</span>{" "}
                Only <span className="font-semibold">verified employers</span> can see candidate phone numbers.
                Get verified from your{" "}
                <Link to="/employer/profile" className="underline hover:text-amber-950">company profile</Link>{" "}
                to unlock direct calls.
              </div>
            </div>
          </Card>
        )}

        {loading ? (
          <div className="text-sm text-zinc-500">Loading…</div>
        ) : candidates.length === 0 ? (
          <Card className="p-10 text-center">
            <Users className="h-8 w-8 mx-auto text-zinc-300 mb-3" />
            <div className="font-display text-lg font-bold text-zinc-950">No candidates match your filters</div>
            <div className="text-sm text-zinc-500 mt-1">Try relaxing some filters or clearing them.</div>
          </Card>
        ) : (
          <div className="space-y-3">
            {candidates.map((c) => (
              <Card key={c.user_id} className="p-5" data-testid={`candidate-card-${c.user_id}`}>
                <div className="flex flex-col lg:flex-row gap-4">
                  {c.picture ? (
                    <img src={c.picture} alt="" className="h-12 w-12 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="h-12 w-12 rounded-full bg-zinc-900 text-white grid place-items-center font-bold flex-shrink-0">
                      {c.name?.[0]?.toUpperCase() || "U"}
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display text-lg font-bold text-zinc-950">{c.name}</span>
                      {c.match_score !== null && c.match_score !== undefined && (
                        <span className="ai-score-badge" data-testid={`candidate-match-${c.user_id}`}>
                          {c.match_score}% match
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-zinc-600 mt-0.5">{c.profile?.headline || c.profile?.target_role || "QA / Test professional"}</div>
                    <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-zinc-500 tag-mono">
                      <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {c.profile?.location || "—"}</span>
                      <span>·</span>
                      <span className="flex items-center gap-1"><TrendingUp className="h-3 w-3" /> {c.profile?.years_experience ?? 0} yrs</span>
                      {c.profile?.expected_salary && (
                        <>
                          <span>·</span>
                          <span>{c.profile.salary_currency || "INR"} {c.profile.expected_salary}</span>
                        </>
                      )}
                      {user?.is_verified_employer && c.profile?.phone && (
                        <>
                          <span>·</span>
                          <a
                            href={`tel:${c.profile.phone}`}
                            data-testid={`candidate-phone-${c.user_id}`}
                            className="flex items-center gap-1 text-[#002FA7] hover:underline"
                          >
                            <Phone className="h-3 w-3" /> {c.profile.phone}
                          </a>
                        </>
                      )}
                    </div>

                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {c.matched_skills?.slice(0, 8).map(s => (
                        <span key={s} className="tag-mono px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded text-[11px]">✓ {s}</span>
                      ))}
                      {c.missing_skills?.slice(0, 5).map(s => (
                        <span key={s} className="tag-mono px-2 py-0.5 bg-amber-50 text-amber-800 border border-amber-200 rounded text-[11px]">⚠ {s}</span>
                      ))}
                      {(c.matched_skills?.length === 0 && c.missing_skills?.length === 0) && (c.profile?.skills || []).slice(0, 8).map(s => (
                        <span key={s} className="tag-mono px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded text-[11px]">{s}</span>
                      ))}
                    </div>
                  </div>

                  <div className="flex lg:flex-col items-end gap-2 flex-shrink-0">
                    <Button
                      size="sm"
                      onClick={() => { setInviteCandidate(c); setInviteOpen(true); }}
                      data-testid={`invite-${c.user_id}`}
                      className="bg-[#002FA7] hover:bg-[#00227A] text-white"
                    >
                      <Send className="h-3.5 w-3.5 mr-1" /> Invite to apply
                    </Button>
                    {c.profile?.resume_path && (
                      <Button size="sm" variant="outline" onClick={()=>downloadResume(c.user_id)} data-testid={`candidate-resume-${c.user_id}`}>
                        <FileText className="h-3.5 w-3.5 mr-1" /> Resume
                      </Button>
                    )}
                    <a
                      href={`mailto:${c.email}?subject=Opportunity at our company`}
                      data-testid={`candidate-email-${c.user_id}`}
                      className="inline-flex items-center text-xs text-[#002FA7] hover:underline tag-mono"
                    >
                      <Mail className="h-3 w-3 mr-1" /> Email
                    </a>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>
      <InviteCandidateDialog open={inviteOpen} onOpenChange={setInviteOpen} candidate={inviteCandidate} />
    </div>
  );
}
